import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Typography,
	Row,
	Col,
	Space,
	notification,
	Divider,
	Tag,
	Popconfirm,
	Empty,
	Spin,
	Alert,
	theme,
	Badge,
	Checkbox,
} from "antd";
import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiShoppingCartDuotone,
	PiArrowLeftDuotone,
	PiGlobeHemisphereEastDuotone,
} from "react-icons/pi";
import {
	requestCreateOrder,
	requestGetServiceList,
	requestGetDomainExtensionList,
	requestGetAddressList,
	requestGetGhubRepositories,
	requireGetGhubRepositoryBranches,
	requestCheckDomaDomainAvailability,
	requestCheckDomaDomainWhois,
} from "../../services/user.service";
import { formatCurrency } from "../../utils/Formatter";
import AppContext from "../../store/app.context";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { useToken } = theme;

function AddOrderPage() {
	// design token
	const context = useContext(AppContext);
	const user = context.user;
	const teams = user.teams;
	const { teamId } = useParams();

	const [form] = Form.useForm();
	const billingAddressId = Form.useWatch("billingAddressId", form);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);

	const [addresses, setAddresses] = useState([]);
	const [services, setServices] = useState([]);
	const [domainExtensions, setDomainExtensions] = useState([]);
	const [repositories, setRepositories] = useState([]);
	const [repositoriesLoading, setRepositoriesLoading] = useState(false);
	const [branches, setBranches] = useState([]);
	const [branchesLoading, setBranchesLoading] = useState(false);

	// Domain availability checking state
	const [insertedDomainName, setInsertedDomainName] = useState("");
	const [selectedDomainExtensionCode, setSelectedDomainExtensionCode] = useState("");
	const [domainCheckLoading, setDomainCheckLoading] = useState(false);
	const [domainAvailability, setDomainAvailability] = useState(null);
	const [lastCheckedDomain, setLastCheckedDomain] = useState("");

	// Design token
	const {
		token: { colorBg, colorBgContainer, colorBorder },
	} = useToken();

	const [orderItems, setOrderItems] = useState([
		{
			key: "instance",
			type: "INSTANCE",
			serviceCode: "",
			planCode: "",
			regionCode: "",
			domainName: "",
			quantity: 1,
			duration: 1, // Default to 1 month
			addons: [],
			metadata: {},
		},
		{
			key: "domain",
			type: "DOMAIN",
			action: "register", // "register" or "transfer"
			extensionCode: "",
			domainName: "",
			quantity: 1,
			duration: 12, // Fixed to 1 year for domains
			eppCode: "", // For domain transfers
			metadata: {
				action: "register",
				eppCode: "",
			},
		},
	]);

	const fetchInitialData = useCallback(async () => {
		await Promise.all([fetchServices(), fetchDomainExtensions(), fetchAddresses()]);
	}, []);

	useEffect(() => {
		fetchInitialData();
	}, [fetchInitialData]);

	useEffect(() => {
		if (billingAddressId) {
			const address = addresses.find((addr) => addr.id === billingAddressId);
			if (address) {
				form.setFieldsValue({
					billingName: address.name,
					billingEmail: address.email,
					billingPhone: address.phone,
					billingAddress: address.street1,
					billingCity: address.city,
					billingState: address.state,
					billingPostalCode: address.postalCode,
					billingCountry: address.country,
				});
			}
		}
	}, [billingAddressId, form]);

	// Auto-check domain availability when domain name and extension are both provided
	useEffect(() => {
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");
		if (insertedDomainName && selectedDomainExtensionCode) {
			const domainExt = domainExtensions.find((ext) => ext.code === selectedDomainExtensionCode);
			const fullDomain = `${insertedDomainName}${domainExt?.name}`;
			// Only auto-check if we haven't checked this domain yet and domain name is at least 3 characters
			if (lastCheckedDomain !== fullDomain && insertedDomainName.length >= 3) {
				// Add a small delay to avoid too many API calls while typing
				const timeoutId = setTimeout(() => {
					checkDomainAvailability(insertedDomainName, selectedDomainExtensionCode);
				}, 1000);

				return () => clearTimeout(timeoutId);
			}
		}
	}, [insertedDomainName, selectedDomainExtensionCode, lastCheckedDomain]);

	const fetchAddresses = () => {
		requestGetAddressList()
			.then((response) => {
				const data = response.data.data || response.data;
				setAddresses(data);
			})
			.catch((error) => {
				notification.error({
					message: "Kesalahan",
					description: "Gagal memuat daftar alamat",
				});
			});
	};

	const fetchServices = () => {
		requestGetServiceList({ limit: 1000 })
			.then((response) => {
				const serviceData = response.data.data || response.data;
				setServices(serviceData);
			})
			.catch((error) => {
				notification.error({
					message: "Kesalahan",
					description: "Gagal memuat daftar layanan",
				});
			});
	};

	const fetchDomainExtensions = () => {
		requestGetDomainExtensionList({ limit: 1000 })
			.then((response) => {
				const data = response.data.data || response.data;
				setDomainExtensions(data);
			})
			.catch((error) => {
				notification.error({
					message: "Kesalahan",
					description: "Gagal memuat daftar ekstensi domain",
				});
			});
	};

	const fetchRepositories = () => {
		// if development, skip fetching, just mock some data
		if (process.env.NODE_ENV === "development") {
			setRepositories([
				{ id: 1, name: "repo-1", fullName: "team/repo-1" },
				{ id: 2, name: "repo-2", fullName: "team/repo-2" },
			]);
			return;
		}
		setRepositoriesLoading(true);
		requestGetGhubRepositories(teamId)
			.then((response) => {
				const data = response.data.data || response.data;
				setRepositories(data);
			})
			.catch((error) => {
				notification.error({
					message: "Kesalahan",
					description: "Gagal memuat daftar repository",
				});
			})
			.finally(() => {
				setRepositoriesLoading(false);
			});
	};

	const fetchBranches = (repoFullName) => {
		// if development, skip fetching, just mock some data
		if (process.env.NODE_ENV === "development") {
			setBranches([
				{ id: 1, name: "branch-1" },
				{ id: 2, name: "branch-2" },
			]);
			return;
		}
		if (!repoFullName) {
			setBranches([]);
			return;
		}
		setBranchesLoading(true);
		setBranches([]);
		// take only repo name
		const repo = repoFullName.split("/").pop();
		requireGetGhubRepositoryBranches(teamId, repo)
			.then((response) => {
				const data = response?.data?.data || response?.data || [];
				setBranches(data);
			})
			.catch(() => {
				notification.error({
					message: "Kesalahan",
					description: "Gagal memuat daftar branch",
				});
			})
			.finally(() => setBranchesLoading(false));
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setLoading(true);

				const validOrderItems = orderItems.filter((item) => {
					if (item.type === "INSTANCE") {
						return item.serviceCode && item.planCode && item.regionCode;
					} else if (item.type === "DOMAIN") {
						return item.domainName && item.extensionCode;
					}
					return false;
				});

				if (validOrderItems.length === 0) {
					notification.error({
						message: "Kesalahan",
						description: "Harap konfigurasi setidaknya satu layanan atau domain",
					});
					setLoading(false);
					return;
				}

				// Check domain availability for registration
				const domainItem = validOrderItems.find((item) => item.type === "DOMAIN");
				if (domainItem) {
					// Check if domain has been checked and is available
					if (!domainAvailability) {
						notification.error({
							message: "Domain Belum Dicek",
							description: "Silakan cek ketersediaan domain terlebih dahulu sebelum melanjutkan.",
						});
						setLoading(false);
						return;
					}

					if (domainItem.action === "register" && domainAvailability.available !== 1) {
						notification.error({
							message: "Domain Tidak Tersedia",
							description: `Domain ${fullDomain} tidak tersedia untuk didaftarkan. Silakan pilih domain lain.`,
						});
						setLoading(false);
						return;
					}

					if (domainItem.action === "transfer") {
						if (!domainItem.eppCode) {
							notification.error({
								message: "EPP Code Diperlukan",
								description: "Silakan masukkan EPP Code untuk transfer domain.",
							});
							setLoading(false);
							return;
						}

						if (domainAvailability.available !== 0) {
							notification.error({
								message: "Domain Tidak Tersedia",
								description: `Domain ${fullDomain} tidak tersedia untuk transfer. Silakan pilih domain lain.`,
							});
							setLoading(false);
							return;
						}
					}
				}

				// Additional validation for APLA.AP: require repository and branch
				const instanceItemForSubmit = validOrderItems.find((item) => item.type === "INSTANCE");
				if (instanceItemForSubmit?.serviceCode === "APLA.AP") {
					if (
						!instanceItemForSubmit?.metadata?.repository ||
						!instanceItemForSubmit?.metadata?.branch
					) {
						notification.error({
							message: "Konfigurasi Kurang",
							description:
								"Untuk layanan APLA.AP, silakan pilih repository dan branch terlebih dahulu.",
						});
						setLoading(false);
						return;
					}
				}

				const orderPayload = {
					teamId: values.teamId,
					currency: values.currency,
					items: validOrderItems.map((item) => {
						if (item.type === "INSTANCE") {
							const baseItem = {
								type: item.type,
								serviceCode: item.serviceCode,
								planCode: item.planCode,
								regionCode: item.regionCode,
								quantity: item.quantity,
								metadata: {
									...item.metadata,
								},
							};

							if (item.domainName) {
								baseItem.domainName = item.domainName;
							}

							if (item.addons && item.addons.length > 0) {
								baseItem.addonCodes = item.addons.map((addon) => addon.code);
							}

							if (validOrderItems.some((it) => it.type === "DOMAIN")) {
								const domainItem = validOrderItems.find((it) => it.type === "DOMAIN");
								baseItem.metadata = {
									...baseItem.metadata,
									domain: domainItem.domainName.includes(".")
										? domainItem.domainName
										: `${domainItem.domainName}${
												domainExtensions.find((ext) => ext.code === domainItem.extensionCode)?.name
										  }`,
								};
							}
							return baseItem;
						} else if (item.type === "DOMAIN") {
							const domainItem = {
								type: item.type,
								domainName: item.domainName.includes(".")
									? item.domainName
									: `${item.domainName}${
											domainExtensions.find((ext) => ext.code === item.extensionCode)?.name
									  }`,
								extensionCode: item.extensionCode,
								quantity: item.quantity,
								metadata: {
									...item.metadata,
									action: item.action,
									eppCode: item.eppCode || "",
								},
							};

							return domainItem;
						}
						return null;
					}),
					billingAddress: {
						name: values.billingName,
						email: values.billingEmail,
						phone: values.billingPhone,
						address: values.billingAddress,
						city: values.billingCity,
						state: values.billingState,
						postalCode: values.billingPostalCode,
						country: values.billingCountry,
					},
					metadata: values.metadata,
				};

				requestCreateOrder(teamId, orderPayload)
					.then((response) => {
						notification.success({
							message: "Berhasil",
							description: "Pesanan berhasil dibuat!",
						});
						const invoice = response.data.data || response.data;
						navigate(`/${teamId}/invoices/${invoice._id}`);
					})
					.catch((error) => {
						notification.error({
							message: "Kesalahan",
							description: error.response?.data?.message || "Gagal membuat pesanan",
						});
					})
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((errorInfo) => {})
			.finally(() => {});
	};

	const updateOrderItem = (key, field, value) => {
		setOrderItems(
			orderItems.map((item) => {
				if (item.key === key) {
					let updatedItem = { ...item, [field]: value };

					// Reset plan, region, addons and duration when service changes
					if (field === "serviceCode") {
						updatedItem.planCode = "";
						updatedItem.regionCode = "";
						updatedItem.addons = [];
						updatedItem.duration = 1; // Reset to default 1 month
						updatedItem.metadata = {}; // Reset metadata when service changes

						// If switching to WEHO, try to sync domain name from domain item
						if (value === "WEHO") {
							const domainItem = orderItems.find((it) => it.type === "DOMAIN");
							if (domainItem && domainItem.domainName) {
								updatedItem.domainName = domainItem.domainName;
							}
						}

						// If switching to APLA.AP, fetch repositories
						if (value === "APLA.AP") {
							fetchRepositories();
						}
					}

					if (field === "domainName") {
						// if domainName is empty string, it means user want to cancel add domain
						if (value === "") {
							updatedItem.domainName = "";
							updatedItem.extensionCode = "";
							updatedItem.action = "register";
							updatedItem.eppCode = "";
							return updatedItem;
						}
						// set the item.extensionCode
						// take the ext, example genc.web.id the ext is .web.id
						const ext = value.includes(".") ? value.substring(value.indexOf(".")) : "";
						updatedItem.extensionCode = domainExtensions.find((e) => e.name === ext)?.code || "";

						// Sync extension code to instance if WEHO is selected
						const instanceItem = orderItems.find((it) => it.type === "INSTANCE");
						if (instanceItem && instanceItem.serviceCode.includes("WEHO")) {
							// We'll update the instance item in the next iteration
							setOrderItems((prev) =>
								prev.map((prevItem) => {
									if (prevItem.key === "instance") {
										return { ...prevItem, domainName: value };
									}
									return prevItem;
								})
							);
						}
					}

					if (field === "action") {
						// reset domain data
						updatedItem.domainName = "";
						updatedItem.extensionCode = "";
						updatedItem.action = value;

						// reset the domain availability state
						setDomainAvailability(null);
						setLastCheckedDomain("");
						setInsertedDomainName("");
						setSelectedDomainExtensionCode("");
					}

					return updatedItem;
				}
				return item;
			})
		);
	};

	const getServiceAddons = (serviceCode) => {
		const service = services.find((s) => s.code === serviceCode);
		return service?.addons || [];
	};

	const updateItemAddons = (itemKey, addons) => {
		setOrderItems(
			orderItems.map((item) => (item.key === itemKey ? { ...item, addons: addons } : item))
		);
	};

	const updateItemMetadata = (itemKey, metadataField, value) => {
		// if metadataField is repository, also clear branch
		setOrderItems(
			orderItems.map((item) => {
				if (item.key !== itemKey) return item;

				const newMetadata = {
					...item.metadata,
					[metadataField]: value || "",
				};

				if (metadataField === "repository") {
					newMetadata.branch = ""; // clear branch when repository changes
				}

				return {
					...item,
					metadata: newMetadata,
				};
			})
		);
	};

	// Domain availability checking function
	const checkDomainAvailability = async (domainName, extensionCode) => {
		const domainExt = domainExtensions.find((ext) => ext.code === extensionCode);
		const fullDomain = `${domainName}${domainExt.name}`;

		if (!domainName || !extensionCode) {
			setDomainAvailability(null);
			setLastCheckedDomain("");
			return;
		}

		// Don't check the same domain again
		if (lastCheckedDomain === fullDomain) {
			return;
		}

		// Mock for development environment
		if (process.env.NODE_ENV === "development") {
			setDomainAvailability({
				name: fullDomain,
				available: 0,
			});
			setLastCheckedDomain(fullDomain);
			updateOrderItem("domain", "domainName", fullDomain); // Sync domain name to order item
			return;
		}

		setDomainCheckLoading(true);
		setDomainAvailability(null);

		try {
			const domainItem = orderItems.find((item) => item.type === "DOMAIN");
			if (!domainItem) {
				notification.error({
					message: "Gagal Cek Domain",
					description: "Anda belum memilih tipe pendaftaran domain.",
					duration: 5,
				});
				setDomainAvailability(null);
				return;
			}

			if (domainItem.action === "register") {
				const response = await requestCheckDomaDomainAvailability(fullDomain);

				if (response.data?.code === 200) {
					setDomainAvailability(response.data.data);
					setLastCheckedDomain(fullDomain);

					// Show notification based on availability
					if (response.data.data.available === 1) {
						updateOrderItem("domain", "domainName", fullDomain); // Sync domain name to order item
						notification.success({
							message: "Domain Tersedia!",
							description: `Domain ${fullDomain} tersedia untuk didaftarkan.`,
							duration: 3,
						});
					} else {
						notification.warning({
							message: "Domain Tidak Tersedia",
							description: `Domain ${fullDomain} sudah digunakan atau tidak tersedia. Pilih "Transfer" untuk memindahkannya ke Cloudiva.`,
							duration: 5,
						});
					}
				}
			} else if (domainItem.action === "transfer") {
				const response = await requestCheckDomaDomainWhois(fullDomain);

				if (response.data?.code === 200) {
					setDomainAvailability(response.data.data);
					setLastCheckedDomain(fullDomain);

					// Show notification based on availability, 0 mean it's registered, we need registered domain for transfer type
					if (response.data.data.available === 0) {
						updateOrderItem("domain", "domainName", fullDomain); // Sync domain name to order item
						notification.success({
							message: "Domain Tersedia untuk Transfer",
							description: `Domain ${fullDomain} tersedia untuk ditransfer, jangan lupa untuk menuliskan kode EPP nya.`,
							duration: 5,
						});
					} else {
						notification.warning({
							message: "Domain Belum Terdaftar",
							description: `Domain ${fullDomain} belum terdaftar, pilih "Daftar Domain Baru" untuk mendaftarkannya.`,
							duration: 5,
						});
					}
				}
			} else {
				notification.error({
					message: "Gagal Cek Domain",
					description: "Anda belum memilih tipe pendaftaran domain.",
					duration: 5,
				});
				setDomainAvailability(null);
			}
		} catch (error) {
			notification.error({
				message: "Gagal Cek Domain",
				description: "Terjadi kesalahan saat mengecek ketersediaan domain. Silakan coba lagi.",
				duration: 5,
			});
			setDomainAvailability(null);
		} finally {
			setDomainCheckLoading(false);
		}
	};

	const calculateItemPrice = (item, withQuantity = true) => {
		if (item.type === "DOMAIN") {
			const extension = domainExtensions.find((ext) => ext.code === item.extensionCode);

			return extension
				? (item.action === "register"
						? extension.price.registration.amount
						: extension.price.transfer.amount) * item.quantity
				: 0;
		} else if (item.type === "INSTANCE") {
			const service = services.find((s) => s.code === item.serviceCode);
			const plan = service?.plans?.find((p) => p.code === item.planCode);
			const region = service?.regions?.find((r) => r.code === item.regionCode);

			if (!plan) return 0;

			let linePriceWithRegion = plan.price.amount;
			if (region) {
				// linePriceWithRegion = plan.price.amount * region.priceMultiplier;
				if (region.price.type === "PERCENTAGE") {
					linePriceWithRegion *= (100 + region.price.amount) / 100;
				} else {
					linePriceWithRegion += region.price.amount;
				}
			}

			const addonPrice = (item.addons || []).reduce((sum, addon) => {
				return (
					sum +
					(addon.price.type === "PERCENTAGE"
						? linePriceWithRegion * (addon.price.amount / 100)
						: addon.price.amount) *
						(addon.quantity || 1)
				);
			}, 0);

			// Apply duration multiplier and discount
			const monthlyPrice = linePriceWithRegion + addonPrice;
			const duration = item.quantity || 1;

			// Apply duration discount
			let discountRate = 0;
			// if (duration >= 36) discountRate = 0.25; // 25% off for 3+ years
			// else if (duration >= 24) discountRate = 0.2; // 20% off for 2+ years
			// else if (duration >= 12) discountRate = 0.15; // 15% off for 1+ year
			// else if (duration >= 6) discountRate = 0.1; // 10% off for 6+ months
			// else if (duration >= 3) discountRate = 0.05; // 5% off for 3+ months

			const discountedMonthlyPrice = monthlyPrice * (1 - discountRate);
			return withQuantity ? discountedMonthlyPrice * duration : discountedMonthlyPrice;
		}

		return 0;
	};

	const calculateOrderSummary = () => {
		const currency = form.getFieldValue("currency") || "USD";
		let subtotal = 0;

		orderItems.forEach((item) => {
			if (item.type === "DOMAIN" && item.domainName && item.extensionCode) {
				const extension = domainExtensions.find((ext) => ext.code === item.extensionCode);
				if (extension) {
					subtotal +=
						(item.action === "register"
							? extension.price.registration.amount
							: extension.price.transfer.amount) * item.quantity;
				}
			} else if (item.type === "INSTANCE" && item.serviceCode && item.planCode && item.regionCode) {
				const service = services.find((s) => s.code === item.serviceCode);
				const plan = service?.plans?.find((p) => p.code === item.planCode);
				const region = service?.regions?.find((r) => r.code === item.regionCode);

				if (plan) {
					let linePriceWithRegion = plan.price.amount;
					if (region) {
						// linePriceWithRegion = plan.price.amount * region.priceMultiplier;
						if (region.price.type === "PERCENTAGE") {
							linePriceWithRegion *= (100 + region.price.amount) / 100;
						} else {
							linePriceWithRegion += region.price.amount;
						}
					}

					const addonPrice = (item.addons || []).reduce((sum, addon) => {
						return (
							sum +
							(addon.price.type === "PERCENTAGE"
								? linePriceWithRegion * (addon.price.amount / 100)
								: addon.price.amount) *
								(addon.quantity || 1)
						);
					}, 0);

					subtotal += (linePriceWithRegion + addonPrice) * item.quantity;
				}
			}
		});

		const tax = subtotal * 0.11;

		const total = subtotal + tax;

		return {
			currency,
			subtotal,
			setupFees: 0,
			tax,
			total,
			itemCount: orderItems.filter(
				(item) =>
					(item.type === "INSTANCE" && item.serviceCode && item.planCode) ||
					(item.type === "DOMAIN" && item.extensionCode && item.domainName)
			).length,
		};
	};

	// Get region flag
	const getRegionFlag = (code) => {
		return <Typography.Text code>{code}</Typography.Text>;
	};

	// Stepper configuration and navigation
	const steps = [
		{
			title: "Layanan",
			description: "Pilih layanan Anda",
			icon: "🚀",
		},
		{
			title: "Domain",
			description: "Konfigurasi domain",
			icon: "🌐",
		},
		{
			title: "Checkout",
			description: "Tinjau & kirim",
			icon: "📋",
		},
	];

	const nextStep = () => {
		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const prevStep = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const canProceedToNextStep = () => {
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");

		switch (currentStep) {
			case 0: // Service step
				// Basic validation
				const basicValidation =
					instanceItem?.serviceCode &&
					instanceItem?.planCode &&
					instanceItem?.regionCode &&
					instanceItem?.duration;

				if (!basicValidation) return false;

				// Additional validation for APLA.AP - require repository and branch
				if (instanceItem?.serviceCode === "APLA.AP") {
					return instanceItem?.metadata?.repository && instanceItem?.metadata?.branch;
				}

				return true;
			case 1: // Domain step
				// For WEHO service, domain is required
				if (instanceItem?.serviceCode === "WEHO") {
					if (!domainItem?.domainName || !domainItem?.extensionCode) {
						return false;
					}
					// If transferring domain, require EPP code
					if (domainItem?.action === "transfer" && !domainItem?.eppCode) {
						return false;
					}
					return true;
				}

				// For non-WEHO services, domain is optional
				// If user started configuring domain, validate it completely
				if (domainItem?.action) {
					if (!domainItem?.domainName || !domainItem?.extensionCode) {
						return false;
					}
					// If transferring domain, require EPP code
					if (domainItem?.action === "transfer" && !domainItem?.eppCode) {
						return false;
					}
				}

				return true;
			case 2: // Checkout step
				return true;
			default:
				return false;
		}
	};

	const shouldShowDomainStep = () => {
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");

		// Always show domain step if WEHO service is selected
		if (instanceItem?.serviceCode === "WEHO") {
			return true;
		}

		// For non-WEHO services, show domain step only if user has started configuring domain
		return domainItem?.domainName || domainItem?.action;
	};

	// Optional Domain Card for Non-WEHO Services
	function renderOptionalDomainCard() {
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");

		return (
			<Card
				style={{ marginBottom: 24 }}
				bordered
				title={
					<Space>
						<PiGlobeHemisphereEastDuotone style={{ fontSize: 20, color: "#01abe9" }} />
						<span>Domain (Opsional)</span>
					</Space>
				}>
				<div style={{ textAlign: "center", marginBottom: 24 }}>
					<div style={{ fontSize: "48px", marginBottom: 16 }}>🌐</div>
					<Title level={5} style={{ margin: 0, marginBottom: 8 }}>
						Tambahkan Domain ke Pesanan Anda?
					</Title>
					<Typography.Text
						type="secondary"
						style={{ fontSize: 14, display: "block", marginBottom: 20 }}>
						Layanan {instanceItem?.serviceCode} tidak memerlukan domain, tapi Anda bisa
						menambahkannya untuk kemudahan pengelolaan.
					</Typography.Text>

					<Space size="large">
						<Button
							size="large"
							type={!domainItem?.domainName ? "primary" : "default"}
							onClick={() => {
								// Clear domain data
								updateOrderItem("domain", "domainName", "");
								setInsertedDomainName("");
								setSelectedDomainExtensionCode("");
								setDomainAvailability(null);
								setLastCheckedDomain("");
							}}>
							❌ Tidak, Terima Kasih
						</Button>
						<Button
							size="large"
							type={domainItem?.domainName ? "primary" : "default"}
							onClick={() => {
								// Initialize domain with default values
								updateOrderItem("domain", "action", "register");
								if (!domainItem?.domainName) {
									// Focus user to start entering domain
									const domainInput = document.querySelector('input[placeholder*="domain"]');
									if (domainInput) {
										domainInput.scrollIntoView({ behavior: "smooth" });
									}
								}
							}}>
							✅ Ya, Tambahkan Domain
						</Button>
					</Space>
				</div>

				{/* Show domain configuration when user chooses to add domain */}
				{domainItem?.action && (
					<div
						style={{ marginTop: 32, padding: "24px", background: "#f8f9fa", borderRadius: "8px" }}>
						<Alert
							type="info"
							showIcon
							message="Konfigurasi Domain"
							description="Silakan konfigurasi domain yang ingin Anda tambahkan ke pesanan ini."
							style={{ marginBottom: 24 }}
						/>
						{renderDomainStep()}
					</div>
				)}
			</Card>
		);
	}

	// Step 1: Service Selection
	function renderServiceStep() {
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const selectedService = services.find((service) => service.code === instanceItem?.serviceCode);

		return (
			<div>
				{/* Service Selection */}
				<Card style={{ marginBottom: 24 }} bordered title="Jenis Layanan">
					<Select
						style={{ width: "100%" }}
						placeholder="Pilih layanan"
						size="large"
						value={instanceItem?.serviceCode || undefined}
						onChange={(value) => updateOrderItem("instance", "serviceCode", value)}>
						{services.map((service) => (
							<Option key={service.code} value={service.code}>
								{service.name} ({service.code})
							</Option>
						))}
					</Select>
					{
						// show service description
						instanceItem?.serviceCode && (
							<Space style={{ margin: "16px 0" }} direction="vertical">
								<Title level={5} style={{ marginBottom: 4 }}>
									{selectedService?.name}
								</Title>
								<Typography.Text type="secondary" code>
									{selectedService?.code}
								</Typography.Text>
								<Typography.Text type="secondary">{selectedService?.description}</Typography.Text>
							</Space>
						)
					}
				</Card>

				{/* Plan Selection */}
				{instanceItem?.serviceCode && (
					<Card style={{ marginBottom: 24 }} bordered title="Pilih Paket">
						<Row gutter={[16, 16]}>
							{selectedService?.plans?.map((plan) => {
								const isSelected = instanceItem?.planCode === plan.code;
								return (
									<Col xs={24} sm={12} md={8} lg={6} key={plan.code}>
										<Badge.Ribbon
											text={isSelected ? "Dipilih" : null}
											style={{ display: isSelected ? "block" : "none" }}>
											<Card
												size="small"
												hoverable
												onClick={() => updateOrderItem("instance", "planCode", plan.code)}
												style={{
													border: `1px solid ${isSelected ? "#01abe9" : colorBorder}`,
												}}>
												<div style={{ textAlign: "center" }}>
													<Title level={5} style={{ margin: 0, fontSize: 16 }}>
														{plan.name}
													</Title>
													<div>
														<div style={{ fontWeight: "bold", fontSize: 18, color: "#01abe9" }}>
															{formatCurrency(plan.price.amount, "IDR")}
															<span style={{ fontSize: 12, color: "#999", fontWeight: "normal" }}>
																/bulan
															</span>
														</div>
														{plan.setupFee > 0 && (
															<div style={{ fontSize: 12, color: "#666" }}>
																Setup: {formatCurrency(plan.setupFee, "IDR")}
															</div>
														)}
													</div>
													{plan.features && (
														<div>
															{plan.features.slice(0, 3).map((feature, idx) => (
																<div
																	key={idx}
																	style={{
																		fontSize: 12,
																		color: "#666",
																		marginBottom: 4,
																		display: "flex",
																		alignItems: "center",
																	}}>
																	<span style={{ color: "#52c41a", marginRight: 6 }}>✓</span>
																	{feature.name}
																</div>
															))}
															{plan.features.length > 3 && (
																<div style={{ fontSize: 12, color: "#999" }}>
																	+{plan.features.length - 3} fitur lainnya
																</div>
															)}
														</div>
													)}
												</div>
											</Card>
										</Badge.Ribbon>
									</Col>
								);
							})}
						</Row>
					</Card>
				)}

				{/* Region Selection */}
				{instanceItem?.serviceCode && instanceItem?.planCode && (
					<Card style={{ marginBottom: 24 }} bordered title="Pilih Wilayah">
						<Row gutter={[16, 16]}>
							{selectedService?.regions?.map((region) => {
								const isSelected = instanceItem?.regionCode === region.code;
								return (
									<Col xs={24} sm={12} md={8} lg={6} key={region.code}>
										<Badge.Ribbon
											text={isSelected ? "Dipilih" : null}
											style={{ display: isSelected ? "block" : "none" }}>
											<Card
												size="small"
												hoverable
												onClick={() => updateOrderItem("instance", "regionCode", region.code)}
												style={{
													border: `1px solid ${isSelected ? "#01abe9" : colorBorder}`,
												}}>
												<div
													style={{
														textAlign: "center",
														display: "flex",
														flexDirection: "row",
														alignItems: "center",
														justifyContent: "center",
														justifyItems: "center",
														gap: 4,
													}}>
													<div
														style={{
															opacity: isSelected ? 1 : 0.7,
														}}>
														{getRegionFlag(region.code) || "🌍"}
													</div>
													<Title level={5} style={{ margin: 0, fontSize: 14 }}>
														{region.name}
														{region.price.amount > 0 && (
															<span style={{ fontSize: 12, color: "#999", marginLeft: 4 }}>
																+
																{region.price.type === "PERCENTAGE"
																	? `${region.price.amount}%`
																	: formatCurrency(region.price.amount, "IDR")}
															</span>
														)}
													</Title>
													<Typography.Text type="secondary" style={{ fontSize: 12 }}>
														{region.location}
													</Typography.Text>
												</div>
											</Card>
										</Badge.Ribbon>
									</Col>
								);
							})}
						</Row>
					</Card>
				)}

				{/* Duration Selection */}
				{instanceItem?.serviceCode && instanceItem?.planCode && instanceItem?.regionCode && (
					<Card style={{ marginBottom: 24 }} bordered title="Pilih Durasi">
						<Row gutter={[16, 16]}>
							{[
								{ duration: 1, label: "1 Bulan", discount: 0 },
								{ duration: 3, label: "3 Bulan", discount: 0 },
								{ duration: 6, label: "6 Bulan", discount: 0 },
								{ duration: 12, label: "1 Tahun", discount: 0 },
							].map((option) => {
								const isSelected = instanceItem?.quantity === option.duration;
								const selectedPlan = selectedService?.plans?.find(
									(p) => p.code === instanceItem?.planCode
								);
								const basePrice = selectedPlan?.price?.amount || 0;
								const discountedPrice = basePrice * (1 - option.discount / 100);
								const totalPrice = discountedPrice * option.duration;
								const savings =
									option.discount > 0 ? (basePrice - discountedPrice) * option.duration : 0;

								return (
									<Col xs={24} sm={12} md={8} lg={6} key={option.duration}>
										<Badge.Ribbon
											text={isSelected ? "Dipilih" : null}
											style={{ display: isSelected ? "block" : "none" }}>
											<Card
												size="small"
												hoverable
												onClick={() => updateOrderItem("instance", "quantity", option.duration)}
												style={{
													border: `1px solid ${isSelected ? "#01abe9" : colorBorder}`,
													position: "relative",
												}}>
												{option.discount > 0 && (
													<div
														style={{
															position: "absolute",
															top: -8,
															right: -8,
															background: "#ff4d4f",
															color: "white",
															borderRadius: "50%",
															width: 32,
															height: 32,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontSize: 10,
															fontWeight: "bold",
														}}>
														-{option.discount}%
													</div>
												)}
												<div style={{ textAlign: "center" }}>
													<Title level={5} style={{ margin: 0, fontSize: 14 }}>
														{option.label}
													</Title>
													<div style={{ fontWeight: "bold", color: "#13c2c2", fontSize: 16 }}>
														{formatCurrency(totalPrice, "IDR")}
													</div>
													<div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
														{formatCurrency(discountedPrice, "IDR")}/month
													</div>
													{savings > 0 && (
														<div
															style={{
																fontSize: 11,
																color: "#52c41a",
																marginTop: 4,
																fontWeight: "bold",
															}}>
															Save {formatCurrency(savings, "IDR")}
														</div>
													)}
													<div style={{ fontSize: 10, color: "#fa8c16", marginTop: 4 }}>
														🔥 Harga Flat!
													</div>
													{/* {option.duration >= 12 && (
													<div style={{ fontSize: 10, color: "#fa8c16", marginTop: 4 }}>
														🔥 Pilihan Terbaik
													</div>
												)} */}
												</div>
											</Card>
										</Badge.Ribbon>
									</Col>
								);
							})}
						</Row>
					</Card>
				)}

				{/* Addons Selection */}
				{instanceItem?.serviceCode &&
					instanceItem?.planCode &&
					instanceItem?.regionCode &&
					instanceItem?.duration && (
						<Card style={{ marginBottom: 24 }} bordered title="Add-ons (Opsional)">
							<Row gutter={[16, 16]}>
								{getServiceAddons(instanceItem.serviceCode)?.map((addon) => {
									const isSelected = instanceItem?.addons?.some((a) => a.code === addon.code);
									return (
										<Col xs={24} sm={12} md={8} lg={6} key={addon.code}>
											<Badge.Ribbon
												text={isSelected ? "Dipilih" : null}
												style={{ display: isSelected ? "block" : "none" }}>
												<Card
													size="small"
													hoverable
													onClick={() => {
														const currentAddons = instanceItem?.addons || [];
														if (isSelected) {
															// Remove addon
															updateItemAddons(
																"instance",
																currentAddons.filter((a) => a.code !== addon.code)
															);
														} else {
															// Add addon
															updateItemAddons("instance", [
																...currentAddons,
																{ ...addon, quantity: 1 },
															]);
														}
													}}
													style={{
														border: `1px solid ${isSelected ? "#01abe9" : colorBorder}`,
													}}>
													<div>
														<div
															style={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "flex-start",
																marginBottom: 8,
															}}>
															<Title level={5} style={{ margin: 0, fontSize: 14 }}>
																{addon.name}
															</Title>
														</div>
														<div style={{ marginBottom: 8 }}>
															<div style={{ fontWeight: "bold", color: "#722ed1" }}>
																+
																{addon.price.type === "PERCENTAGE"
																	? `${addon.price.amount}%`
																	: formatCurrency(addon.price.amount, "IDR")}
																<span style={{ fontSize: 12, color: "#999", fontWeight: "normal" }}>
																	/month
																</span>
															</div>
															{addon.setupFee > 0 && (
																<div style={{ fontSize: 12, color: "#666" }}>
																	Setup: {formatCurrency(addon.setupFee, "IDR")}
																</div>
															)}
														</div>
														{addon.description && (
															<Typography.Text type="secondary" style={{ fontSize: 12 }}>
																{addon.description}
															</Typography.Text>
														)}
													</div>
												</Card>
											</Badge.Ribbon>
										</Col>
									);
								})}
							</Row>
							{getServiceAddons(instanceItem.serviceCode)?.length === 0 && (
								<div style={{ textAlign: "center", color: "#999", padding: 24 }}>
									Tidak ada add-on yang tersedia untuk layanan ini
								</div>
							)}
						</Card>
					)}

				{/* Repository Selection for APLA.AP */}
				{instanceItem?.serviceCode === "APLA.AP" &&
					instanceItem?.planCode &&
					instanceItem?.regionCode &&
					instanceItem?.duration && (
						<Card style={{ marginBottom: 24 }} bordered title="Repository GitHub">
							{repositoriesLoading ? (
								<div style={{ textAlign: "center", padding: 32 }}>
									<div style={{ color: "#01abe9", fontSize: 16 }}>Memuat repository...</div>
								</div>
							) : repositories.length > 0 ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
									<Select
										size="large"
										style={{ width: "100%" }}
										placeholder="Pilih repository untuk deployment"
										value={instanceItem?.metadata?.repository}
										onChange={async (value) => {
											// Reset and fetch branches
											setBranches([]);
											updateItemMetadata("instance", "repository", value);
											fetchBranches(value);
										}}
										optionLabelProp="label"
										showSearch
										filterOption={(input, option) =>
											option.label.toLowerCase().includes(input.toLowerCase())
										}>
										{repositories.map((repo) => (
											<Select.Option
												key={repo.id}
												value={repo.fullName}
												label={`${repo.name} (${repo.visibility})`}>
												<div style={{ display: "flex", flexDirection: "column" }}>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
														}}>
														<span style={{ fontWeight: "bold" }}>{repo.name}</span>
														<Tag color={repo.visibility === "PRIVATE" ? "red" : "green"}>
															{repo.visibility}
														</Tag>
													</div>
													<Typography.Text type="secondary" style={{ fontSize: 12 }}>
														{repo.fullName}
													</Typography.Text>
												</div>
											</Select.Option>
										))}
									</Select>

									<Select
										size="large"
										disabled={!instanceItem?.metadata?.repository}
										loading={branchesLoading}
										style={{ width: "100%" }}
										placeholder="Pilih branch"
										value={instanceItem?.metadata?.branch || undefined}
										onDropdownVisibleChange={(open) => {
											if (
												open &&
												instanceItem?.metadata?.repository &&
												branches.length === 0 &&
												!branchesLoading
											) {
												fetchBranches(instanceItem?.metadata?.repository);
											}
										}}
										onChange={(value) => {
											updateItemMetadata("instance", "branch", value);
										}}
										showSearch
										filterOption={(input, option) =>
											(option?.label || "").toLowerCase().includes(input.toLowerCase())
										}
										optionLabelProp="label">
										{(branches || []).map((br) => {
											const name = br?.name || br;
											return (
												<Select.Option key={name} value={name} label={name}>
													<div style={{ display: "flex", justifyContent: "space-between" }}>
														<span style={{ fontWeight: 500 }}>{name}</span>
														<Typography.Text type="secondary" style={{ fontSize: 12 }}>
															Branch
														</Typography.Text>
													</div>
												</Select.Option>
											);
										})}
									</Select>
								</div>
							) : (
								<Alert
									type="warning"
									showIcon
									message="Tidak ada repository yang tersedia"
									description="Pastikan Anda memiliki akses repository di akun GitHub Anda."
									banner
									style={{ marginBottom: 24 }}
									action={
										<Button
											type="primary"
											size="small"
											onClick={() => {
												navigate(`/${teamId}/settings?tab=integrations`);
											}}>
											Sambungkan GitHub
										</Button>
									}
								/>
							)}
							<Typography.Text
								type="secondary"
								style={{ fontSize: 12, marginTop: 8, display: "block" }}>
								💡 Pilih repository dan branch yang akan di-deploy dengan layanan APLA.AP ini
							</Typography.Text>
						</Card>
					)}
			</div>
		);
	}

	// Step 2: Konfigurasi Domain
	function renderDomainStep() {
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");
		const isWehoService = instanceItem?.serviceCode === "WEHO";
		const isDomainRequired = isWehoService;

		return (
			<Card
				bordered={false}
				style={{ marginBottom: 24 }}
				title={
					<Space>
						{/* <PiGlobeHemisphereEastDuotone style={{ fontSize: 20, color: "#01abe9" }} /> */}
						<span>Konfigurasi Domain</span>
						{isDomainRequired && <Tag color="red">Wajib</Tag>}
						{!isDomainRequired && <Tag color="blue">Opsional</Tag>}
					</Space>
				}>
				{/* Show helpful message for required vs optional */}
				{isDomainRequired && (
					<Alert
						type="info"
						showIcon
						message="Domain Diperlukan"
						description={`Layanan ${instanceItem?.serviceCode} memerlukan domain untuk berfungsi dengan baik. Silakan pilih dan konfigurasi domain Anda.`}
						style={{ marginBottom: 24 }}
					/>
				)}

				{!isDomainRequired && (
					<Alert
						type="success"
						showIcon
						message="Domain Opsional"
						description={`Anda memilih untuk menambahkan domain ke layanan ${instanceItem?.serviceCode}. Domain tidak wajib, tapi bisa memudahkan pengelolaan.`}
						style={{ marginBottom: 24 }}
						action={
							<Button
								size="small"
								type="text"
								onClick={() => {
									// Clear domain data and go back
									updateOrderItem("domain", "domainName", "");
									setInsertedDomainName("");
									setSelectedDomainExtensionCode("");
									setDomainAvailability(null);
									setLastCheckedDomain("");
								}}>
								Batalkan Domain
							</Button>
						}
					/>
				)}
				{/* Pilihan Aksi Domain */}
				<div style={{ marginBottom: 32 }}>
					<Title level={5} style={{ marginBottom: 16 }}>
						Aksi Domain
					</Title>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12}>
							<Badge.Ribbon
								text={domainItem?.action === "register" ? "Dipilih" : null}
								style={{ display: domainItem?.action === "register" ? "block" : "none" }}>
								<Card
									hoverable
									onClick={() => updateOrderItem("domain", "action", "register")}
									style={{
										border: `1px solid ${
											domainItem?.action === "register" ? "#01abe9" : colorBorder
										}`,
									}}>
									<div style={{ textAlign: "center" }}>
										<div style={{ fontSize: "48px" }}>🆕</div>
										<Title level={5} style={{ margin: 0 }}>
											Daftar Domain Baru
										</Title>
										<Typography.Text type="secondary" style={{ fontSize: 14 }}>
											Daftarkan nama domain baru
										</Typography.Text>
									</div>
								</Card>
							</Badge.Ribbon>
						</Col>
						<Col xs={24} sm={12}>
							<Badge.Ribbon
								text={domainItem?.action === "transfer" ? "Dipilih" : null}
								style={{ display: domainItem?.action === "transfer" ? "block" : "none" }}>
								<Card
									hoverable
									onClick={() => updateOrderItem("domain", "action", "transfer")}
									style={{
										border: `1px solid ${
											domainItem?.action === "transfer" ? "#01abe9" : colorBorder
										}`,
									}}>
									<div style={{ textAlign: "center" }}>
										<div style={{ fontSize: "48px" }}>🔄</div>
										<Title level={5} style={{ margin: 0 }}>
											Transfer Domain yang Ada
										</Title>
										<Typography.Text type="secondary" style={{ fontSize: 14 }}>
											Transfer domain dari registrar lain
										</Typography.Text>
									</div>
								</Card>
							</Badge.Ribbon>
						</Col>
					</Row>
				</div>

				{/* Input Nama Domain dan Ekstensi */}
				{domainItem?.action && (
					<div style={{ marginBottom: 32 }}>
						<Title level={5} style={{ marginBottom: 16 }}>
							Nama Domain {isDomainRequired && <span style={{ color: "#ff4d4f" }}>*</span>}
						</Title>

						<Row gutter={[12, 12]}>
							<Col xs={24} md={16}>
								<Space.Compact style={{ width: "100%" }}>
									<Input
										style={{ width: "60%" }}
										size="large"
										placeholder="Masukkan nama domain (contoh: websiteku)"
										value={insertedDomainName || ""}
										onChange={(e) => {
											const domainName = e.target.value.replaceAll(/[^a-zA-Z0-9-]/g, "");
											// updateOrderItem("domain", "domainName", domainName);
											setInsertedDomainName(domainName);
											// Reset domain availability when domain name changes
											if (domainName !== insertedDomainName) {
												setDomainAvailability(null);
												setLastCheckedDomain("");
											}
										}}
									/>
									<Select
										style={{ width: "40%" }}
										size="large"
										placeholder="Pilih ekstensi"
										value={selectedDomainExtensionCode}
										onChange={(value) => {
											// updateOrderItem("domain", "extensionCode", value);
											// Reset domain availability when extension changes
											setDomainAvailability(null);
											setLastCheckedDomain("");
											setSelectedDomainExtensionCode(value);
										}}
										optionLabelProp="label">
										<Select.Option value={""} label="Pilih ekstensi">
											<div style={{ display: "flex", alignItems: "center" }}>
												<span style={{ color: "#999" }}>Pilih ekstensi</span>
											</div>
										</Select.Option>
										{domainExtensions.map((extension) => (
											<Select.Option
												key={extension.code}
												value={extension.code}
												label={`${extension.name} - ${formatCurrency(
													extension.price.registration.amount,
													"IDR"
												)}/tahun`}>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
													}}>
													<span style={{ fontWeight: "bold" }}>{extension.name}</span>
													<span style={{ color: "#01abe9", fontWeight: "bold" }}>
														{formatCurrency(extension.price.registration.amount, "IDR")}/tahun
													</span>
												</div>
												{extension.description && (
													<div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
														{extension.description}
													</div>
												)}
											</Select.Option>
										))}
									</Select>
								</Space.Compact>
							</Col>

							{/* Domain Check Button */}
							<Col xs={24} md={8}>
								<Button
									type="primary"
									size="large"
									loading={domainCheckLoading}
									disabled={!insertedDomainName || !selectedDomainExtensionCode}
									onClick={() =>
										checkDomainAvailability(insertedDomainName, selectedDomainExtensionCode)
									}
									style={{ width: "100%" }}>
									{domainCheckLoading ? "Mengecek..." : "Cek Ketersediaan"}
								</Button>
							</Col>
						</Row>

						{/* Domain Availability Status */}
						{domainAvailability && (
							<Alert
								style={{ marginTop: 12 }}
								type={
									domainItem.action === "register"
										? domainAvailability.available === 1
											? "success"
											: "error"
										: domainAvailability.available === 0
										? "success"
										: "error"
								}
								showIcon
								message={
									domainItem.action === "register"
										? domainAvailability.available === 1
											? `Domain ${domainAvailability.name} tersedia untuk didaftarkan!`
											: `Domain ${domainAvailability.name} tidak tersedia untuk didaftarkan`
										: domainAvailability.available === 0
										? `Domain ${domainAvailability.name} tersedia untuk ditransfer!`
										: `Domain ${domainAvailability.name} tidak tersedia untuk ditransfer`
								}
							/>
						)}

						<Typography.Text
							type="secondary"
							style={{ fontSize: 12, marginTop: 8, display: "block" }}>
							💡{" "}
							{domainItem?.action === "register"
								? "Masukkan nama domain dan pilih ekstensi, lalu klik 'Cek Ketersediaan' untuk memastikan domain tersedia."
								: "Masukkan nama domain lengkap dengan ekstensi untuk transfer."}{" "}
							Semua domain didaftarkan selama 1 tahun.
							{!isDomainRequired && " Domain ini opsional untuk layanan yang Anda pilih."}
						</Typography.Text>
					</div>
				)}

				{/* EPP Code Input (for transfers only) */}
				{domainItem?.action === "transfer" && domainItem?.domainName && (
					<div style={{ marginBottom: 32 }}>
						<Title level={5} style={{ marginBottom: 16 }}>
							Kode Otorisasi EPP <span style={{ color: "#ff4d4f" }}>*</span>
						</Title>
						<Input
							size="large"
							placeholder="Masukkan kode EPP/Authorization dari registrar saat ini"
							value={domainItem?.eppCode || ""}
							onChange={(e) => updateOrderItem("domain", "eppCode", e.target.value)}
							style={{ fontSize: 16 }}
						/>
						<Typography.Text
							type="secondary"
							style={{ fontSize: 12, marginTop: 8, display: "block" }}>
							💡 Anda bisa mendapatkan kode ini dari registrar domain saat ini. Biasanya ditemukan
							di panel manajemen domain atau dengan menghubungi dukungan.
						</Typography.Text>
					</div>
				)}

				{/* Domain Preview */}
				{domainAvailability &&
					domainItem?.domainName &&
					((domainItem?.action === "register" && domainItem?.extensionCode) ||
						domainItem?.action === "transfer") && (
						<div style={{ marginTop: 32 }}>
							<Card
								style={{
									border: `1px solid ${domainItem?.action === "transfer" ? "#01abe9" : "#52c41a"}`,
								}}>
								<div style={{ textAlign: "center" }}>
									<Title
										level={4}
										style={{
											margin: 0,
											color: domainItem?.action === "transfer" ? "#01abe9" : "#52c41a",
										}}>
										{domainItem?.action === "transfer" ? "🔄 Transfer" : "🌐 Registrasi Baru"}
									</Title>
									<div
										style={{
											fontSize: 24,
											fontWeight: "bold",
											color: domainItem?.action === "transfer" ? "#01abe9" : "#52c41a",
										}}>
										{domainItem.domainName}
									</div>
									<Typography.Text type="secondary" style={{ marginTop: 8, display: "block" }}>
										{domainItem?.action === "transfer"
											? "Domain akan ditransfer dari registrar saat ini. Pastikan data benar, kami tidak akan me-refund dana bila kesalahan pada input Anda."
											: "Domain ini akan didaftarkan selama 1 tahun"}
									</Typography.Text>
									{domainItem?.action === "transfer" && domainItem?.eppCode && (
										<Tag color="green" style={{ marginTop: 12 }}>
											<Typography.Text>✓ Kode EPP sudah dimasukkan</Typography.Text>
										</Tag>
									)}
								</div>
							</Card>
						</div>
					)}
			</Card>
		);
	}

	// Step 3: Checkout
	function renderCheckoutStep() {
		const orderSummary = calculateOrderSummary();
		const instanceItem = orderItems.find((item) => item.type === "INSTANCE");
		const domainItem = orderItems.find((item) => item.type === "DOMAIN");

		return (
			<div>
				{/* <div style={{ marginBottom: 24 }}>
					<Title level={5} style={{ margin: 0, color: "#01abe9" }}>
						📋 Tinjau Pesanan Anda
					</Title>
					<Typography.Text type="secondary">
						Tinjau konfigurasi Anda dan selesaikan pesanan
					</Typography.Text>
				</div> */}

				<Row gutter={24}>
					<Col span={24}>
						{/* Item Pesanan */}
						<Card title="Item Pesanan" style={{ marginBottom: 24 }}>
							<Space direction="vertical" style={{ width: "100%" }} size="large">
								{/* Instance Item */}
								{instanceItem?.serviceCode && instanceItem?.planCode && (
									<Card size="small">
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
											}}>
											<div style={{ flex: 1 }}>
												<Title level={5} style={{ margin: 0 }}>
													🖥️{" "}
													{services.find((s) => s.code === instanceItem.serviceCode)?.name ||
														instanceItem.serviceCode}
												</Title>
												<div style={{ marginTop: 8 }}>
													<Tag color="blue">{instanceItem.planCode}</Tag>
													<Tag color="orange">{instanceItem.regionCode}</Tag>
													{instanceItem.domainName && (
														<Tag color="green">{instanceItem.domainName}</Tag>
													)}
													{instanceItem?.metadata?.repository && (
														<Tag color="geekblue">{instanceItem?.metadata?.repository}</Tag>
													)}
													{instanceItem?.metadata?.branch && (
														<Tag color="cyan">{instanceItem?.metadata?.branch}</Tag>
													)}
												</div>
												{instanceItem.addons && instanceItem.addons.length > 0 && (
													<div style={{ marginTop: 12 }}>
														<Typography.Text type="secondary" strong>
															Add-ons:
														</Typography.Text>
														<div style={{ marginTop: 4 }}>
															{instanceItem.addons.map((addon, idx) => (
																<Tag key={idx} color="purple" style={{ marginBottom: 4 }}>
																	{addon.name}
																</Tag>
															))}
														</div>
													</div>
												)}
											</div>
											<div style={{ textAlign: "right" }}>
												{/* base price */}
												<Typography.Text type="secondary" style={{ fontSize: 12 }}>
													{formatCurrency(calculateItemPrice(instanceItem, false), "IDR")}/bulan
												</Typography.Text>
												<div style={{ fontSize: 18, fontWeight: "bold", color: "#01abe9" }}>
													{formatCurrency(calculateItemPrice(instanceItem), "IDR")}
												</div>
												<Typography.Text type="secondary" style={{ fontSize: 12 }}>
													untuk {instanceItem.quantity} bulan
												</Typography.Text>
											</div>
										</div>
									</Card>
								)}

								{/* Domain Item */}
								{domainItem?.domainName && domainItem?.extensionCode && (
									<Card size="small">
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
											}}>
											<div style={{ flex: 1 }}>
												<Title level={5} style={{ margin: 0 }}>
													🌐 {domainItem.action === "register" ? "Domain Baru" : "Transfer Domain"}
												</Title>
												<div style={{ marginTop: 8 }}>
													<Tag color="green">{domainItem.domainName}</Tag>
												</div>
											</div>
											<div style={{ textAlign: "right" }}>
												{/* base price */}
												<Typography.Text type="secondary" style={{ fontSize: 12 }}>
													{formatCurrency(calculateItemPrice(domainItem, false), "IDR")}/tahun
												</Typography.Text>
												<div style={{ fontSize: 18, fontWeight: "bold", color: "#01abe9" }}>
													{formatCurrency(calculateItemPrice(domainItem), "IDR")}
												</div>
												<Typography.Text type="secondary" style={{ fontSize: 12 }}>
													untuk {domainItem.quantity} tahun
												</Typography.Text>
											</div>
										</div>
									</Card>
								)}

								{/* If empty */}
								{(!instanceItem?.planCode || !instanceItem.planCode) && !domainItem?.domainName && (
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description="Tidak ada item pesanan"
									/>
								)}
							</Space>
						</Card>

						{/* Ringkasan Pesanan */}
						<Card
							title="Ringkasan Pesanan"
							style={{
								// background: "linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 20%)",
								border: "1px solid #01abe9",
								marginBottom: 24,
								// position: "sticky",
								// top: 152,
							}}>
							<div style={{ marginBottom: 16 }}>
								<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
									<span>Subtotal ({orderSummary.itemCount} item)</span>
									<strong>{formatCurrency(orderSummary.subtotal, orderSummary.currency)}</strong>
								</div>
								{orderSummary.setupFees > 0 && (
									<div
										style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
										<span>Setup Fees</span>
										<strong>{formatCurrency(orderSummary.setupFees, orderSummary.currency)}</strong>
									</div>
								)}
								{orderSummary.tax > 0 && (
									<div
										style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
										<span>PPN (11%)</span>
										<strong>{formatCurrency(orderSummary.tax, orderSummary.currency)}</strong>
									</div>
								)}
								<Divider style={{ margin: "12px 0" }} />
								<div style={{ display: "flex", justifyContent: "space-between", fontSize: 18 }}>
									<strong>Total</strong>
									<strong style={{ color: "#01abe9" }}>
										{formatCurrency(orderSummary.total, orderSummary.currency)}
									</strong>
								</div>
							</div>

							<Typography.Text
								type="secondary"
								style={{ fontSize: 12, display: "block", textAlign: "center" }}>
								💳 Pembayaran akan diproses setelah konfirmasi pesanan
							</Typography.Text>
						</Card>

						{/* Informasi Penagihan */}
						<Card title="Informasi Penagihan" style={{ marginBottom: 24 }}>
							<Row gutter={16}>
								<Col span={24}>
									<Form.Item
										label="Alamat Penagihan"
										name="billingAddressId"
										rules={[{ required: true, message: "Silakan pilih alamat penagihan" }]}>
										<Select
											placeholder="Pilih alamat penagihan"
											showSearch
											filterOption={(input, option) =>
												option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
											}>
											{addresses.map((address) => (
												<Option key={address.id} value={address.id}>
													{address.name} - {address.street1}, {address.city}, {address.state}{" "}
													{address.zip}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={16}>
								<Col span={12}>
									<Form.Item
										label="Tim"
										name="teamId"
										initialValue={teamId}
										rules={[{ required: true, message: "Silakan pilih tim" }]}>
										<Select
											disabled
											placeholder="Pilih tim"
											showSearch
											filterOption={(input, option) =>
												option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
											}>
											{teams.map((team) => (
												<Option key={team._id} value={team._id}>
													{`${team.name}`}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label="Mata Uang" name="currency" rules={[{ required: true }]}>
										<Select>
											<Option value="IDR">IDR (Indonesian Rupiah)</Option>
											{/* <Option value="USD">USD (US Dollar)</Option> */}
										</Select>
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={16}>
								<Col span={12}>
									<Form.Item
										label="Nama Penagihan"
										name="billingName"
										rules={[{ required: true, message: "Silakan masukkan nama penagihan" }]}>
										<Input placeholder="Nama lengkap untuk penagihan" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Email Penagihan"
										name="billingEmail"
										rules={[
											{ required: true, message: "Silakan masukkan email penagihan" },
											{ type: "email", message: "Silakan masukkan email yang valid" },
										]}>
										<Input placeholder="Alamat email" />
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={16}>
								<Col span={12}>
									<Form.Item
										label="Telepon"
										name="billingPhone"
										rules={[{ required: true, message: "Silakan masukkan nomor telepon" }]}>
										<Input placeholder="Nomor telepon" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Negara"
										name="billingCountry"
										rules={[{ required: true, message: "Silakan masukkan negara" }]}>
										<Input placeholder="Negara" />
									</Form.Item>
								</Col>
							</Row>

							<Form.Item
								label="Alamat"
								name="billingAddress"
								rules={[{ required: true, message: "Silakan masukkan alamat" }]}>
								<TextArea rows={3} placeholder="Alamat lengkap" />
							</Form.Item>

							<Row gutter={16}>
								<Col span={8}>
									<Form.Item
										label="Kota"
										name="billingCity"
										rules={[{ required: true, message: "Silakan masukkan kota" }]}>
										<Input placeholder="Kota" />
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										label="Provinsi"
										name="billingState"
										rules={[{ required: true, message: "Silakan masukkan provinsi" }]}>
										<Input placeholder="State/Province" />
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										label="Kode Pos"
										name="billingPostalCode"
										rules={[{ required: true, message: "Silakan masukkan kode pos" }]}>
										<Input placeholder="Kode pos" />
									</Form.Item>
								</Col>
							</Row>

							<Form.Item
								label="Metadata (JSON)"
								name="metadata"
								extra="Data tambahan dalam format JSON">
								<TextArea rows={3} placeholder="{}" />
							</Form.Item>
						</Card>

						<Popconfirm
							title="Apakah Anda yakin ingin membuat pesanan?"
							onConfirm={handleSubmit}
							okText="Ya"
							placement="top"
							cancelText="Tidak">
							<Button
								type="primary"
								htmlType="button"
								loading={loading}
								style={{ width: "100%" }}
								icon={<PiShoppingCartDuotone />}
								size="large">
								Buat Pesanan
							</Button>
						</Popconfirm>
					</Col>
				</Row>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/orders")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						🚀 Buat Pesanan Baru
					</Title>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<>
					<Form
						form={form}
						layout="vertical"
						initialValues={{
							currency: "IDR",
							metadata: "{}",
						}}>
						<Row gutter={24}>
							<Col xs={24} md={16}>
								{renderServiceStep()}
								{shouldShowDomainStep() && renderDomainStep()}
								{/* Optional Domain Selection for Non-WEHO Services */}
								{orderItems.find((item) => item.type === "INSTANCE")?.serviceCode &&
									!orderItems
										.find((item) => item.type === "INSTANCE")
										?.serviceCode.includes("WEHO") &&
									!shouldShowDomainStep() &&
									renderOptionalDomainCard()}
							</Col>
							<Col xs={24} md={8}>
								{renderCheckoutStep()}
							</Col>
						</Row>
					</Form>
				</>
			</div>
		</div>
	);
}

export default AddOrderPage;
